import { useLazyQuery } from '@apollo/client/react';
import { type FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { type OrderLookupQuery, type OrderLookupQueryVariables } from '../../__generated__/graphql-client-types';
import { ORDER_RECEIPT_LINK, ORDER_SHIPMENTS_LINK } from '../../constants/links';
import {
	emailInputRegistrationConfig,
	orderNumberInputRegistrationConfig,
	zipCodeInputRegistrationConfig
} from '../../helpers/form-validation-helper/form-validation-helper';
import { useNavigation } from '../../hooks/navigation/navigation.hooks';
import { useRecaptcha } from '../../hooks/recaptcha/recaptcha.hooks';
import { useRouterLocation } from '../../hooks/routing/location.hook';
import { ORDER_LOOKUP } from '../../queries/order/order-lookup.queries';
import { StyledButton } from '../buttons';
import { AlertNotification } from '../common-components/alert-notification/alert-notification.component';
import { Label } from '../common-components/label/label.component';
import { Loading } from '../common-components/loading/loading.component';
import { Recaptcha } from '../common-components/recaptcha/recaptcha.component';
import { TextInput } from '../inputs/input/text-input/text-input.component';
import { PhoneNumberInput } from '../phone-number-input/phone-number-input.component';

export const OrderLookupError: FunctionComponent = () => (
	<div className="mt3 w-100" data-testid="order-lookup-error">
		<AlertNotification alertType={'danger'} size={'LARGE'}>
			<ul className="list pl0 ma0 lh-copy">
				<li className="font-medium">At least one of the above numbers are incorrect.</li>
				<li>Find the Order Number within your Confirmation Email.</li>
				<li>Find the PO Number on your Packing Slip. Ignore anything before the dash if there is one.</li>
			</ul>
		</AlertNotification>
	</div>
);

const MarketPlaceOrder: FunctionComponent = () => (
	<div className="bg-theme-warning-light f5">
		<ul className="list pa3">
			<li>
				Orders placed via third party will not display here. Please contact the site where the original order was placed to view the
				order details.
			</li>
		</ul>
	</div>
);

type FormInputs = {
	orderNumber: string;
	zipCode: string;
	verifyBy: 'Phone' | 'Email';
	email: string;
	phone: string;
};

export type OrderLookupFormProps = {
	navigateToPage?: 'receipt' | 'shipments';
	helpTextTooltip?: React.ReactElement | string;
};

export const OrderLookupForm: FunctionComponent<OrderLookupFormProps> = ({ navigateToPage = 'receipt', helpTextTooltip }) => {
	const navigate = useNavigation();
	const urlParams = new URLSearchParams(useRouterLocation().search);
	const qOrderNumber = urlParams.get('orderNumber') || undefined;
	const qZip = urlParams.get('zip') || undefined;

	const { getToken } = useRecaptcha('order_lookup');

	const [orderLookup, { loading, data }] = useLazyQuery<OrderLookupQuery, OrderLookupQueryVariables>(ORDER_LOOKUP);

	const onSubmit = async (formSubmissionData: FormInputs) => {
		const token = await getToken();
		const { zipCode, orderNumber, email, phone } = formSubmissionData;
		const { data: dataValue } = await orderLookup({
			variables: { orderNumber: Number(orderNumber), zipCode, token, phone, email }
		});
		if (dataValue?.orderLookup.__typename === 'OrderLookup' && !dataValue?.orderLookup.isMarketplaceOrder) {
			const formData = getValues();
			navigate(
				`${navigateToPage === 'shipments' ? ORDER_SHIPMENTS_LINK : ORDER_RECEIPT_LINK}/${dataValue.orderLookup.orderNumber}?zip=${
					formData.zipCode
				}`
			);
		}
	};

	const {
		control,
		register,
		handleSubmit,
		formState: { errors },
		getValues,
		watch
	} = useForm<FormInputs>({
		mode: 'onChange',
		defaultValues: {
			orderNumber: qOrderNumber,
			zipCode: qZip,
			verifyBy: 'Email'
		}
	});

	const verifyByPhone = watch('verifyBy') === 'Phone';

	return (
		<div className="relative">
			<form onSubmit={handleSubmit(onSubmit)} data-testid="order-lookup-form" aria-label="order lookup form">
				<TextInput
					className="font-medium"
					label="Order or PO Number"
					placeholder="ex. 708887376 or 870834970"
					autoComplete="off"
					data-testid="orderNumberInput"
					invalid={Boolean(errors.orderNumber)}
					invalidMessage={errors.orderNumber?.message}
					helpTextTooltip={helpTextTooltip ? helpTextTooltip : ''}
					automationHook="reference-id"
					required={true}
					{...register('orderNumber', { ...orderNumberInputRegistrationConfig, required: true })}
				/>
				<TextInput
					className="font-medium mt3"
					label="Billing Postal Code"
					placeholder="ex. 95928"
					autoComplete="off"
					required={true}
					data-testid="zipCodeInput"
					invalid={Boolean(errors.zipCode)}
					invalidMessage={errors.zipCode?.message}
					automationHook="zip"
					{...register('zipCode', { ...zipCodeInputRegistrationConfig, required: true })}
				/>
				<Label className="font-medium mt3" label="Customer Email or Phone" id="contactBy" required={true}>
					<>
						<label htmlFor="verifyByEmail" className="f6 normal">
							<input id="verifyByEmail" type="radio" value="Email" {...register('verifyBy')} />
							Email
						</label>
						<label htmlFor="verifyByPhone" className="f6 normal">
							<input
								id="verifyByPhone"
								type="radio"
								value="Phone"
								className="ml2"
								data-testid="verifyPhoneInput"
								{...register('verifyBy')}
							/>
							Phone
						</label>
					</>
				</Label>
				{verifyByPhone ? (
					<PhoneNumberInput
						control={control}
						errors={errors}
						required={true}
						hideLabel={true}
						name="phone"
						formName="order-lookup-form"
						testId="phoneInput"
					/>
				) : (
					<TextInput
						ariaLabel="email input"
						data-testid="emailInput"
						placeholder="ex. my.email@company.com"
						invalid={Boolean(errors.email)}
						invalidMessage={errors.email?.message}
						required={true}
						{...register('email', emailInputRegistrationConfig)}
					/>
				)}

				{data?.orderLookup.__typename === 'OrderLookup' && data?.orderLookup?.isMarketplaceOrder && <MarketPlaceOrder />}
				{data?.orderLookup.__typename === 'NotFoundError' && <OrderLookupError />}

				<Recaptcha />

				<div className="mt3">
					<StyledButton buttonType="submit" disabled={loading} automationHook="find-my-order">
						Find My Order
					</StyledButton>
				</div>
			</form>
			{loading && (
				<div className={`flex absolute top-0 bottom-0 left-0 right-0 items-center justify-center f1 bg-theme-white-o70`}>
					<Loading />
				</div>
			)}
		</div>
	);
};
